/**
 * 로컬 저장 데이터의 계정 귀속 (FN-061 · 오픈이슈 OI-48).
 *
 * **왜 필요한가**: `authStore.clear()` 는 토큰만 지운다. 그래서 같은 기기에서 계정을
 * 바꾸면 **이전 사용자의 최근 스캔·최근 검색어가 그대로 보였다.** 컬렉션·보유는
 * Phase 3 에서 서버 API 로 넘어가 이 문제가 없지만, 아래 두 가지는 아직 localStorage 다.
 *
 * **정책** (2026-08-28 전수조사 H6):
 *   - **로그아웃만으로는 지우지 않는다** — 같은 사람이 다시 들어올 자리다
 *   - **다른 계정이 로그인하면** 이전 계정분을 지운다
 *   - **탈퇴하면** 전부 지운다 (`clearOwnedLocalData`)
 *   - **게스트 스캔 카운트는 어느 경우에도 유지한다** — "기기 기준 평생 5회"(§4.1)가
 *     정책이라, 계정을 갈아타 체험 횟수를 되살리는 구멍을 만들지 않는다
 *
 * ⚠ **소유자 기록이 없으면 지우지 않는다.** 비회원이 스캔한 뒤 처음 로그인하는 흐름
 *   (§4.3 "비회원 기록을 계정으로 이전")에서 방금 쌓은 기록을 지워버리기 때문이다.
 *   첫 로그인은 "남의 것을 물려받는" 경우가 아니라 "내 것에 이름을 붙이는" 경우다.
 */
import { __RECENT_SCANS_KEY__, clearRecentScans } from "./recentScans";

/** 현재 로컬 데이터의 주인 (회원 일련번호 문자열). 없으면 = 아직 주인이 정해지지 않음. */
const OWNER_KEY = "peeka.localDataOwner";

/**
 * 최근 검색어 (SC-001). 정본을 여기 두는 이유 — 소유자 정리 대상 목록과 저장 키가
 * 갈리면 한쪽만 고쳐져 조용히 안 지워진다. SearchPage 가 이 상수를 가져다 쓴다.
 */
export const RECENT_SEARCHES_KEY = "peeka.recentSearches";

function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * 계정에 귀속되는 로컬 데이터를 모두 비운다 (탈퇴 · 계정 전환 공용).
 *
 * 게스트 스캔 카운트·표시 설정(언어·통화·테마)은 대상이 아니다 — 사람이 아니라
 * 기기에 붙는 값이다.
 */
export function clearOwnedLocalData(): void {
  // 최근 스캔은 구독자(홈 §4)가 있어 키를 직접 지우면 화면이 안 따라온다.
  clearRecentScans();
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(RECENT_SEARCHES_KEY);
    s.removeItem(OWNER_KEY);
  } catch {
    /* private mode 등 — 지우지 못해도 흐름을 깨지 않는다 */
  }
}

/**
 * 로그인한 회원이 이 기기 로컬 데이터의 주인인지 대조하고, 아니면 이전 주인의 것을 비운다.
 *
 * user 가 확정되는 모든 경로(새 로그인 · 콜드스타트 부트스트랩 · 프로필 갱신)에서
 * 불린다. 같은 주인이면 아무 일도 하지 않는다.
 */
export function reconcileLocalDataOwner(memberSerialNo: number): void {
  const s = storage();
  if (!s || !Number.isFinite(memberSerialNo)) return;
  const next = String(memberSerialNo);
  let prev: string | null = null;
  try {
    prev = s.getItem(OWNER_KEY);
  } catch {
    return;
  }
  if (prev === next) return;

  // prev 가 null = 주인이 아직 없다(비회원이 쓰던 기기이거나 이 앱의 첫 로그인).
  // 지우지 않고 이름만 붙인다 — 위 ⚠ 참조.
  if (prev !== null) clearOwnedLocalData();

  try {
    s.setItem(OWNER_KEY, next);
  } catch {
    /* 저장 못 하면 다음 로그인에 한 번 더 대조할 뿐이라 무해 */
  }
}

export const __LOCAL_DATA_OWNER_KEY__ = OWNER_KEY;
